import React, { useEffect } from 'react';
import DocumentVenteForm from '../components/Forms/DocumentVenteForm';
import { Typography, Divider, Box, Chip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LigneDocumentVenteForm from '../components/Forms/LigneDocumentVenteForm';
import LigneDocumentVente from './LigneDocumentVente';
import { useDispatch, useSelector } from 'react-redux';
import { useGenerateReport } from '../hooks/useGenerateReport';
import { FormatRapport, TypeRapport } from '../constants/enums';
import PrintMenuButton from '../components/PrintMenuButton';
import MyButton from '../components/MyButton';
import { useDialog } from '../components/DialogComponent';
import { setSelectedLigne } from '../reduxStore/LigneDocumentVenteSlice';
import { cloturerDocumentVente, decloturerDocumentVente } from '../reduxStore/documentVenteSlice';
import { AppDispatch } from '../reduxStore/Store';

/** Miroir front de CoutAcquisitionService.affecteCMUPAchat — vrai si le doc est une réception d'achat fournisseur. */
function isDocAchat(typeDoc: any): boolean {
  if (!typeDoc) return false;
  return Number(typeDoc.affecte_stock) === 1
    && Number(typeDoc.signe ?? 0) > 0
    && String(typeDoc.type_tiers ?? '').toUpperCase() === 'FOURNISSEUR'
    && Number(typeDoc.est_fabrication ?? 0) !== 1;
}

const DocumentVente: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { selectedDocument, cloturing, decloturing } = useSelector((state: any) => state.DocumentVenteSlice);
  const { selectedLigne }    = useSelector((state: any) => state.LigneDocumentVenteSlice);
  const selectedDepartement  = useSelector((state: any) => state.AppSlice.selectedDepartement);
  const { generateReport, generating } = useGenerateReport();

  // Comparaison truthy plutôt que `=== 1` strict : le driver mariadb peut retourner
  // TINYINT(1) comme Number/BigInt/Boolean selon le contexte de jointure de la SP.
  // `cloture` ne peut valoir que 0 ou 1 → !! couvre tous les types.
  const isCloture = !!selectedDocument?.cloture;

  const [LigneDialog, openLigneDialog, closeLigneDialog] = useDialog({
    modal: true,
    dialogProps: { maxWidth: 'sm', fullWidth: true },
  });

  // Quand le doc change ou se ferme, on ferme tout dialog ligne en cours +
  // on purge `selectedLigne` — sinon le useDialog garde son isOpen=true et
  // le dialog réapparaît à la sélection d'un autre doc.
  useEffect(() => {
    closeLigneDialog();
    dispatch(setSelectedLigne(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDocument?.id]);

  const handleAddLigne = () => {
    dispatch(setSelectedLigne(null));
    openLigneDialog();
  };

  // Édition explicite : open systématique, indépendant de la référence
  // de selectedLigne (sinon re-clic sur la même ligne ne fire pas).
  const handleEditLigne = (ligne: any) => {
    dispatch(setSelectedLigne(ligne));
    openLigneDialog();
  };

  const handleCloseLigne = () => {
    dispatch(setSelectedLigne(null));
    closeLigneDialog();
  };

  const handlePrint = (format: FormatRapport, template_id?: number | null) => {
    if (!selectedDocument?.numero_doc) return;
    generateReport({
      type_rapport:   TypeRapport.IMPRESSION_DOCUMENT,
      format,
      parametres:     {
        numero_doc:         selectedDocument.numero_doc,
        document_id:        selectedDocument.id ?? null,
        departement_id:     selectedDocument.departement_id ?? null,
        type_document_code: selectedDocument.type_document?.code ?? null,
      },
      template_id:    template_id ?? null,
      departement_id: selectedDepartement?.id ?? null,
      fileName:       selectedDocument.numero_doc,
      key:            selectedDocument.numero_doc,
    });
  };

  const handleCloturer = () => {
    if (!selectedDocument?.numero_doc || isCloture || cloturing) return;
    const msg = isDocAchat(selectedDocument.type_document)
      ? "Clôturer ce document ? Il ne sera plus modifiable et le prix de revient sera recalculé."
      : "Clôturer ce document ? Il ne sera plus modifiable.";
    if (!window.confirm(msg)) return;
    dispatch(cloturerDocumentVente({
      document_vente_id: selectedDocument.id,
      numero_doc:        selectedDocument.numero_doc,
      doc_code:          selectedDocument.type_document?.code ?? selectedDocument.doc_code ?? '',
    }));
  };

  const handleDecloturer = () => {
    if (!selectedDocument?.numero_doc || !isCloture || decloturing) return;
    if (!window.confirm("Déclôturer ce document ? Il redeviendra modifiable.")) return;
    dispatch(decloturerDocumentVente({
      document_vente_id: selectedDocument.id,
      numero_doc:        selectedDocument.numero_doc,
      doc_code:          selectedDocument.type_document?.code ?? selectedDocument.doc_code ?? '',
    }));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={sectionLabelStyle}>
          En-tête du document
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedDocument && (
            <Chip
              size="small"
              icon={isCloture ? <LockIcon /> : undefined}
              color={isCloture ? 'success' : 'default'}
              variant={isCloture ? 'filled' : 'outlined'}
              label={isCloture ? 'Clôturé' : 'Brouillon'}
            />
          )}
          {selectedDocument?.numero_doc && !isCloture && (
            <MyButton variant="outlined" size="small" onClick={handleCloturer} disabled={cloturing}>
              {cloturing ? 'Clôture…' : 'Clôturer'}
            </MyButton>
          )}
          {selectedDocument?.numero_doc && isCloture && (
            <MyButton variant="outlined" size="small" onClick={handleDecloturer} disabled={decloturing}>
              {decloturing ? 'Déclôture…' : 'Déclôturer'}
            </MyButton>
          )}
          {selectedDocument?.numero_doc && (
            <PrintMenuButton
              onPdf={(tid)   => handlePrint(FormatRapport.PDF, tid)}
              onExcel={(tid) => handlePrint(FormatRapport.EXCEL, tid)}
              loading={generating}
              typeRapport={TypeRapport.IMPRESSION_DOCUMENT}
              typeDocumentCode={selectedDocument.type_document?.code ?? null}
            />
          )}
        </Box>
      </Box>
      <DocumentVenteForm />

      {selectedDocument && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" sx={sectionLabelStyle}>
              Lignes du document
            </Typography>
            <MyButton variant="contained" size="small" onClick={handleAddLigne} disabled={isCloture}>
              + Ajouter une ligne
            </MyButton>
          </Box>
          <Box sx={{ mt: 1 }}>
            <LigneDocumentVente onEdit={handleEditLigne} />
          </Box>
          <LigneDialog>
            <LigneDocumentVenteForm onCancel={handleCloseLigne} />
          </LigneDialog>
        </>
      )}
    </Box>
  );
};

const sectionLabelStyle = {
  mb: 1,
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  fontSize: '0.72rem',
  fontWeight: 600,
};

export default DocumentVente;
